import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

interface SMSCodeInputProps {
  code: string;
  onCodeChange: (code: string) => void;
  onResend: () => Promise<void>;
  error?: string | null;
  disabled?: boolean;
  autoResendDelay?: number; // in seconds
}

export const SMSCodeInput = ({
  code,
  onCodeChange,
  onResend,
  error,
  disabled = false,
  autoResendDelay = 60
}: SMSCodeInputProps) => {
  const [resendCountdown, setResendCountdown] = useState(autoResendDelay);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (resendCountdown <= 0) return;

    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCountdown]);

  const handleResend = async () => {
    setIsResending(true);
    try {
      await onResend();
      setResendCountdown(autoResendDelay);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Enter the 6-digit code from your phone</Label>
        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={onCodeChange}
            disabled={disabled}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResend}
          disabled={resendCountdown > 0 || isResending || disabled}
          className="text-sm"
        >
          {isResending
            ? "Sending..."
            : resendCountdown > 0
            ? `Resend code in ${resendCountdown}s`
            : "Resend code"}
        </Button>
      </div>
    </div>
  );
};
